/**
 * Reusable role permission utilities for frontend
 * Centralizes role-based permission checks to prevent code duplication
 */

import {
  INDIVIDUAL_ROLE_ID,
  NCG_ROLE_ID,
  RFP_ROLE_ID,
  SECRETARIAT_ROLE_ID,
  BRC_ROLE_ID,
  BIRDLIFE_SECRETARIAT_ROLE_ID
} from 'constants/roles'

/**
 * Roles that can create new sites or initiate reassessments
 */
export const CAN_CREATE_SITES_ROLES = [INDIVIDUAL_ROLE_ID, NCG_ROLE_ID]

/**
 * Roles that can view and manage users
 */
export const CAN_MANAGE_USERS_ROLES = [SECRETARIAT_ROLE_ID, RFP_ROLE_ID, BRC_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID]

/**
 * Roles that can review and approve/reject proposed sites
 */
export const CAN_REVIEW_SITES_ROLES = [RFP_ROLE_ID, BRC_ROLE_ID]

/**
 * Roles that can confirm sites (final approval)
 * Note: BirdLife Secretariat can only confirm IBA sites (checked separately)
 */
export const CAN_CONFIRM_SITES_ROLES = [SECRETARIAT_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID]

/**
 * Roles that can view sensitive data and all statuses in their region
 */
export const CAN_VIEW_SENSITIVE_ROLES = [
  SECRETARIAT_ROLE_ID,
  RFP_ROLE_ID,
  NCG_ROLE_ID,
  BRC_ROLE_ID,
  BIRDLIFE_SECRETARIAT_ROLE_ID
]

/**
 * Roles that cannot update sites data
 */
export const CAN_NOT_VIEW_UPDATE_SITES_DATA_ROLES = [
  SECRETARIAT_ROLE_ID,
  RFP_ROLE_ID,
  BRC_ROLE_ID,
  BIRDLIFE_SECRETARIAT_ROLE_ID
]

/**
 * Roles that are secretariat-type (can confirm sites, manage users, etc.)
 */
export const SECRETARIAT_TYPE_ROLES = [SECRETARIAT_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID]

/**
 * Roles that use CountriesOfInterest for regional filtering (RFP and BRC)
 */
export const REGIONAL_COORDINATOR_ROLES = [RFP_ROLE_ID, BRC_ROLE_ID]

/**
 * Roles that can access blob document explorer
 * Note: intentionally limited to Secretariat and RFP only.
 */
export const CAN_ACCESS_BLOB_EXPLORER_ROLES = [SECRETARIAT_ROLE_ID, RFP_ROLE_ID]

/**
 * Resolve the active role id from a profile object.
 * Prefers nested Role.id (used by sidebar and some API payloads) and falls back to RoleId.
 */
export const getProfileRoleId = (profile) => {
  const raw = profile?.Role?.id ?? profile?.RoleId
  if (raw === undefined || raw === null || raw === '') {
    return null
  }
  const roleId = Number(raw)
  return Number.isNaN(roleId) ? null : roleId
}

/**
 * Check if a role can create sites
 */
export const canCreateSites = (roleId) => {
  return CAN_CREATE_SITES_ROLES.includes(Number(roleId))
}

/**
 * Check if a role can manage users
 */
export const canManageUsers = (roleId) => {
  return CAN_MANAGE_USERS_ROLES.includes(Number(roleId))
}

/**
 * Check if a role can review sites
 */
export const canReviewSites = (roleId) => {
  return CAN_REVIEW_SITES_ROLES.includes(Number(roleId))
}

/**
 * Check if a role can confirm sites
 */
export const canConfirmSites = (roleId) => {
  return CAN_CONFIRM_SITES_ROLES.includes(Number(roleId))
}

/**
 * Check if a role can view sensitive data
 */
export const canViewSensitive = (roleId) => {
  return CAN_VIEW_SENSITIVE_ROLES.includes(roleId)
}

/**
 * Check if the current user has FULL sensitive access at this site.
 * True when:
 * - roles that can view sensitive -> true
 * - proposer OR approved FULL viewer row (exposed by backend) -> true
 * - PARTIAL viewer rows should NOT make this true
 */
export const canViewSensitiveSpeciesAtSiteFullAccess = (site, roleId) => {
  if (canViewSensitive(roleId)) {
    return true
  }

  const fullAccess =
    site?.currentUserHasFullSensitiveAccessAtSite === true ||
    site?.current_user_has_full_sensitive_access_at_site === true ||
    site?.SitesHistory?.currentUserHasFullSensitiveAccessAtSite === true ||
    site?.SitesHistory?.current_user_has_full_sensitive_access_at_site === true

  return fullAccess === true
}

const getSiteCoProposers = (site) => {
  if (Array.isArray(site?.User?.SiteCoProposers)) {
    return site.User.SiteCoProposers
  }
  if (Array.isArray(site?.SiteCoProposers)) {
    return site.SiteCoProposers
  }
  return []
}

const hasBackendSiteAssociationFlags = (site) => {
  return site?.currentUserIsCoProposer === true || site?.currentUserIsProposer === true
}

/**
 * Check if the current user should be able to see a sensitive site row.
 * Sensitive sites are visible to sensitive-enabled roles, plus proposer/co-proposer exceptions.
 */
export const canViewSensitiveSiteRow = (site, profile) => {
  const roleId = profile?.RoleId
  const userId = profile?.id

  if (!site) {
    return false
  }

  const isSensitiveSite = site?.sensitiveSite === true || site?.sensitive_site === true
  if (!isSensitiveSite) {
    return true
  }

  if (canViewSensitive(roleId)) {
    return true
  }

  const proposerIds = [
    site?.ProposerId,
    site?.proposerId,
    site?.UserId,
    site?.User?.id,
    site?.SitesHistory?.ProposerId,
    site?.SitesHistory?.UserId,
    site?.sites_history?.proposer_id,
    site?.sites_history?.user_id
  ].filter(Boolean)
  const isProposer = proposerIds.some((id) => String(id) === String(userId))
  if (isProposer) {
    return true
  }

  if (hasBackendSiteAssociationFlags(site)) {
    return true
  }

  const isCoProposer = getSiteCoProposers(site).some((coProposer) => {
    const coProposerId = coProposer?.UserId || coProposer?.userId || coProposer?.id
    return String(coProposerId) === String(userId)
  })

  return isCoProposer
}

/**
 * Guard My Sites visibility for individual users.
 * For non-individual roles, backend scoping is authoritative.
 */
export const canViewSiteInMySites = (site, profile) => {
  if (!site) {
    return false
  }

  const roleId = profile?.RoleId
  const userId = profile?.id

  // During initial profile hydration, avoid showing rows in My Sites
  // until we know which user/role is signed in.
  if (roleId === undefined || roleId === null) {
    return false
  }

  if (roleId !== INDIVIDUAL_ROLE_ID) {
    return true
  }

  if (userId === undefined || userId === null) {
    return false
  }

  const proposerIds = [
    site?.ProposerId,
    site?.proposerId,
    site?.UserId,
    site?.User?.id,
    site?.SitesHistory?.ProposerId,
    site?.SitesHistory?.UserId,
    site?.sites_history?.proposer_id,
    site?.sites_history?.user_id
  ].filter(Boolean)
  const isProposer = proposerIds.some((id) => String(id) === String(userId))
  if (isProposer) {
    return true
  }

  if (hasBackendSiteAssociationFlags(site)) {
    return true
  }

  const isCoProposer = getSiteCoProposers(site).some((coProposer) => {
    const coProposerId = coProposer?.UserId || coProposer?.userId || coProposer?.id
    return String(coProposerId) === String(userId)
  })

  return isCoProposer
}

/**
 * Sites list (Sites management): Individual users must pass sensitive visibility
 * on every row. When My Sites is ON, backend association scope is authoritative.
 * When catalog (toggle off), defence-in-depth allows all non-sensitive API rows.
 */
export const canIndividualViewSiteInSitesList = (site, profile, isMySiteToggle) => {
  if (!site || profile?.RoleId !== INDIVIDUAL_ROLE_ID) {
    return true
  }
  if (!canViewSensitiveSiteRow(site, profile)) {
    return false
  }
  return true
}

/**
 * Check if a role is a regional coordinator (uses CountriesOfInterest)
 */
export const isRegionalCoordinator = (roleId) => {
  return REGIONAL_COORDINATOR_ROLES.includes(roleId)
}

/**
 * Check if a role cannot update sites data
 */
export const canNotViewUpdateSitesData = (roleId) => {
  return CAN_NOT_VIEW_UPDATE_SITES_DATA_ROLES.includes(roleId)
}

/**
 * Check if a role is a secretariat-type role
 */
export const isSecretariatType = (roleId) => {
  return SECRETARIAT_TYPE_ROLES.includes(roleId)
}

/**
 * Check if a role is BirdLife Secretariat
 */
export const isBirdLifeSecretariat = (roleId) => {
  return roleId === BIRDLIFE_SECRETARIAT_ROLE_ID
}

/**
 * Check if a role can access blob explorer
 */
export const canAccessBlobExplorer = (roleId) => {
  return CAN_ACCESS_BLOB_EXPLORER_ROLES.includes(roleId)
}

